/**
 * components/hazard/common.js — Хамтын тогтмолууд / туслах функцууд
 */
import React from "react";

export const RED = "#E2382E";
export const PAGE_BG = "#F1F3F6";

export const SEV_COLOR = {
  1: "#16A34A",
  2: "#CA8A04",
  3: "#EA580C",
  4: "#DC2626",
  5: "#991B1B",
};

export const PEOPLE_OPTIONS = ["1 хүн", "2-5 хүн", "5-10 хүн", "10-аас дээш"];

/**
 * Лавлахад хадгалсан icon нь FontAwesome КЛАСС мөн эсэх.
 *
 * xab.hazard_categories.icon нь түүхэн шалтгаанаар 3 хэлбэртэй байж болно:
 *   "fa-walking" / "fas fa-walking" — FontAwesome (ЭНЭ нь стандарт)
 *   "🧪"                            — emoji
 *   "accessibility_new"             — Material нэр (энэ төсөлд ачаалагддаггүй)
 * Тиймээс `"fas " + icon` гэж шууд наахаас өмнө үргэлж шалгана.
 */
export function isFaIconName(s) {
  return /^(fa[srlbd]?\s+)?fa-[a-z0-9-]+$/i.test(String(s || "").trim());
}

export function iconForCategory(name) {
  // FontAwesome 5.11 нэр ашиглана. FA6 only нэрсийг (person-walking,
  // gears, person-falling, volume-high, triangle-exclamation) FA5-д таарах
  // хувилбараар сольсон.
  const n = (name || "").toLowerCase();
  if (/хими|бодис|хор|цацраг/.test(n))
    return { icon: "fa-flask", color: "#DC2626" };
  if (/физик/.test(n)) return { icon: "fa-atom", color: "#0EA5E9" };
  if (/биолог|нян|вирус|бактер/.test(n))
    return { icon: "fa-biohazard", color: "#16A34A" };
  if (/эргоном/.test(n))
    return { icon: "fa-walking", color: "#8B5CF6" };
  if (/сэтгэ|стресс|нийгэ/.test(n))
    return { icon: "fa-brain", color: "#EC4899" };
  if (/цахилгаан|гүйдэл/.test(n)) return { icon: "fa-bolt", color: "#CA8A04" };
  if (/механик|машин|тоног/.test(n))
    return { icon: "fa-cogs", color: "#0891B2" };
  if (/унал|өндөр|өндр/.test(n))
    return { icon: "fa-user-injured", color: "#EA580C" };
  if (/гал|шатах|дэлбэр/.test(n)) return { icon: "fa-fire", color: "#EF4444" };
  if (/дуу|чимээ|шуугиан/.test(n))
    return { icon: "fa-volume-up", color: "#6366F1" };
  if (/тоос|агаар/.test(n)) return { icon: "fa-smog", color: "#64748B" };
  if (/халуун|хүйтэн|дулаан/.test(n))
    return { icon: "fa-temperature-high", color: "#F59E0B" };
  if (/бусад|өөр/.test(n)) return { icon: "fa-shapes", color: "#64748B" };
  return { icon: "fa-exclamation-triangle", color: "#0EA5E9" };
}

// Section толгой
/**
 * Группийн гарчиг.
 *
 * ⚠ `required` — ЗААВАЛ БӨГЛӨХ талбарыг УЛААН ОДООР тэмдэглэнэ
 *   (2026-11-05, хэрэглэгч: «заавал бөглөх талбаруудыг улаан одоор
 *   тэмдэглэ»). Серверийн `FINDING_REQUIRED` (тайлбар · хугацаа ·
 *   хариуцагч · ангилал)-тай ИЖИЛ байх ёстой — эс бөгөөс хэрэглэгч
 *   од байхгүй талбар алгасаад зөрчил нь «Ноорог (дутуу)» болно.
 */
export function SectionTitle({ children, right, required }) {
  return (
    <div
      /* ⚠ ЖИЖИГРҮҮЛЭВ (2026-11-05, хэрэглэгч: «групп хэсгийн гарчиг
         хэсгийг жижигрүүл»). Зөрчил бүртгэх цонхонд 8-9 групп байдаг
         тул гарчиг тус бүр 12px + 10px зай эзэлж, маягтын эзлэхүүний
         мэдэгдэхүйц хувийг гарчиг «идэж» байв.
         ⚠ Томруулахдаа ХОЁУЛАНГ нь хар: `fontSize` ба `marginBottom` —
           зөвхөн нэгийг засвал хэмнэл эвдэрнэ. */
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 6,
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: 0.4,
          color: "#8A94A6",
          textTransform: "uppercase",
        }}
      >
        {children}
        {required ? (
          <b
            title="Заавал бөглөнө — дутуу бол зөрчил «Ноорог (дутуу)» төлөвт орж, шалгалт хаагдахгүй"
            style={{ color: "#DC2626", marginLeft: 4, fontSize: 13 }}
          >
            *
          </b>
        ) : null}
      </span>
      {right != null && (
        <span style={{ fontSize: 12, color: "#A8AFBC" }}>{right}</span>
      )}
    </div>
  );
}

// Card обертка
export function Card({ children, style }) {
  return (
    <>
      <style>{`
        @media (max-width: 600px) {
          .hz-card { padding: 12px !important; margin-bottom: 10px !important; }
          .ant-modal-body { padding: 12px !important; }
          .ant-modal-header { padding: 10px 14px !important; }
          .ant-modal-title { font-size: 13px !important; }
          .ant-modal-content { border-radius: 12px !important; }
          .ant-modal { padding-bottom: 0 !important; }
        }
        @media (max-width: 420px) {
          .hz-card { padding: 10px !important; }
        }
      `}</style>
      <div
        className="hz-card"
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #E6E9EF",
          padding: 16,
          marginBottom: 14,
          // CSS multi-column (Register.js .hz-grid) доторх картыг багана
          // хооронд хуваагдахаас сэргийлнэ — эс тэгвэл заагт таарсан
          // textarea/input Chrome/Safari дээр focus авахгүй, бичихгүй болдог.
          breakInside: "avoid",
          WebkitColumnBreakInside: "avoid",
          pageBreakInside: "avoid",
          ...style,
        }}
      >
        {children}
      </div>
    </>
  );
}

export const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid #DDE1E8",
  fontSize: 14,
  outline: "none",
  background: "#fff",
  color: "#1F2430",
};

/**
 * Аюулын БҮХ сонгосон төрлийг backend-ийн `hazard_category_ids` формат
 * (таслалаар тусгаарласан текст) руу хөрвүүлнэ.
 *
 * HazardFormComposer нь 1-3 төрөл сонгуулж `categoryIds` массивт хадгалдаг
 * бөгөөд `categoryId` нь зөвхөн ЭХНИЙХ (backward-compatible). Тиймээс
 * createHazard дуудахдаа зөвхөн `categoryId` илгээвэл 2, 3 дахь төрөл
 * замдаа алдагдана.
 *
 * @param {{categoryIds?: Array, categoryId?: number|string}} form
 * @returns {string|undefined} "1,4" эсвэл сонголтгүй бол undefined
 */
export function hazardCategoryIds(form) {
  const ids =
    form?.categoryIds && form.categoryIds.length
      ? form.categoryIds
      : form?.categoryId != null && form.categoryId !== ""
        ? [form.categoryId]
        : [];
  const clean = ids
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  return clean.length ? [...new Set(clean)].join(",") : undefined;
}
